import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Vehicle } from '../../types/vehicle';
import { reservationService } from '../../services/reservationService';
import { format, addHours, differenceInMinutes, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';

interface TimelineProps {
  fleetId: string;
  vehicles: Vehicle[];
  date: Date; // Date à afficher
  startHour?: number; // Heure de début (défaut: 8)
  endHour?: number; // Heure de fin (défaut: 20)
}

interface TimeSlot {
  startTime: Date;
  endTime: Date;
  isOccupied: boolean;
}

const FleetAvailabilityTimeline: React.FC<TimelineProps> = ({
  fleetId,
  vehicles,
  date,
  startHour = 8,
  endHour = 20
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [vehicleTimeslots, setVehicleTimeslots] = useState<Map<string, TimeSlot[]>>(new Map());

  useEffect(() => {
    loadAvailability();
  }, [fleetId, date, vehicles]);

  const loadAvailability = async () => {
    setLoading(true);

    try {
      const dayStart = startOfDay(date);
      const timelineStart = addHours(dayStart, startHour);
      const timelineEnd = addHours(dayStart, endHour);

      // Récupérer toutes les réservations pour la flotte pour cette journée
      const availability = await reservationService.getVehiclesAvailabilityForPeriod(
        fleetId,
        timelineStart,
        timelineEnd
      );

      // Construire les time slots pour chaque véhicule
      const timeslotsMap = new Map<string, TimeSlot[]>();

      vehicles.forEach(vehicle => {
        const vehicleReservations = availability.find(
          a => a.vehicleId === vehicle.id
        )?.reservations || [];

        // Créer une liste continue de time slots
        const slots: TimeSlot[] = [];
        let currentTime = new Date(timelineStart);

        // Trier les réservations par date de début
        const sortedReservations = vehicleReservations.sort(
          (a, b) => a.startDate.getTime() - b.startDate.getTime()
        );

        sortedReservations.forEach(reservation => {
          // Ajuster les dates de réservation pour qu'elles soient dans la plage de la timeline
          const resStart = reservation.startDate < timelineStart ? timelineStart : reservation.startDate;
          const resEnd = reservation.endDate > timelineEnd ? timelineEnd : reservation.endDate;

          // Si il y a un espace libre avant cette réservation
          if (currentTime < resStart) {
            slots.push({
              startTime: new Date(currentTime),
              endTime: new Date(resStart),
              isOccupied: false
            });
          }

          // Ajouter le créneau occupé
          if (resStart < resEnd) {
            slots.push({
              startTime: new Date(resStart),
              endTime: new Date(resEnd),
              isOccupied: true
            });
          }

          currentTime = new Date(resEnd);
        });

        // Si il reste du temps libre après la dernière réservation
        if (currentTime < timelineEnd) {
          slots.push({
            startTime: new Date(currentTime),
            endTime: new Date(timelineEnd),
            isOccupied: false
          });
        }

        // Si aucune réservation, tout est libre
        if (slots.length === 0) {
          slots.push({
            startTime: new Date(timelineStart),
            endTime: new Date(timelineEnd),
            isOccupied: false
          });
        }

        timeslotsMap.set(vehicle.id, slots);
      });

      setVehicleTimeslots(timeslotsMap);
    } catch (error) {
      console.error('Error loading fleet availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimePosition = (time: Date): number => {
    const dayStart = startOfDay(date);
    const timelineStart = addHours(dayStart, startHour);
    const timelineEnd = addHours(dayStart, endHour);

    const totalMinutes = differenceInMinutes(timelineEnd, timelineStart);
    const minutesFromStart = differenceInMinutes(time, timelineStart);

    return (minutesFromStart / totalMinutes) * 100;
  };

  const getSlotWidth = (slot: TimeSlot): number => {
    const dayStart = startOfDay(date);
    const timelineStart = addHours(dayStart, startHour);
    const timelineEnd = addHours(dayStart, endHour);

    const totalMinutes = differenceInMinutes(timelineEnd, timelineStart);
    const slotMinutes = differenceInMinutes(slot.endTime, slot.startTime);

    return (slotMinutes / totalMinutes) * 100;
  };

  const generateHourMarkers = () => {
    const markers = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      const position = ((hour - startHour) / (endHour - startHour)) * 100;
      const isFullHour = hour % 2 === 0;

      markers.push(
        <Box
          key={hour}
          sx={{
            position: 'absolute',
            left: `${position}%`,
            top: 0,
            bottom: 0,
            borderLeft: isFullHour ? '1px solid #bdbdbd' : '1px dashed #e0e0e0',
            pointerEvents: 'none',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: -20,
              left: -12,
              fontSize: '0.75rem',
              fontWeight: isFullHour ? 600 : 400,
              color: isFullHour ? 'text.primary' : 'text.secondary',
            }}
          >
            {hour}h
          </Typography>
        </Box>
      );
    }
    return markers;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const formatSlotTime = (date: Date) => {
    return format(date, 'HH:mm', { locale: fr });
  };

  const formatSlotTooltip = (slot: TimeSlot) => {
    const duration = differenceInMinutes(slot.endTime, slot.startTime);
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    const durationStr = hours > 0
      ? `${hours}h${minutes > 0 ? minutes + 'min' : ''}`
      : `${minutes}min`;

    return `${formatSlotTime(slot.startTime)} - ${formatSlotTime(slot.endTime)} (${durationStr})`;
  };

  return (
    <Box>
      {/* En-tête avec légende */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1, minWidth: 300 }}>
          {t('reservation:calendar.legend', 'Affichage Occupé/Libre uniquement (sans identité du réservant)')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            size="small"
            sx={{
              bgcolor: '#2e7d32',
              color: 'white',
              fontWeight: 500,
              '& .MuiChip-label': { px: 1.5 }
            }}
            label={t('reservation:calendar.free', 'Libre')}
          />
          <Chip
            size="small"
            sx={{
              bgcolor: '#d32f2f',
              color: 'white',
              fontWeight: 500,
              '& .MuiChip-label': { px: 1.5 }
            }}
            label={t('reservation:calendar.occupied', 'Occupé')}
          />
        </Box>
      </Box>

      {/* Timeline */}
      <Paper sx={{ p: 2, overflow: 'auto' }}>
        <Box sx={{ minWidth: 800 }}>
          {/* Axe des heures */}
          <Box sx={{ position: 'relative', height: 30, mb: 2 }}>
            {generateHourMarkers()}
          </Box>

          {/* Lignes de véhicules */}
          {vehicles.map((vehicle, vehicleIndex) => {
            const slots = vehicleTimeslots.get(vehicle.id) || [];

            return (
              <Box key={vehicle.id} sx={{ mb: 3 }}>
                {/* Nom du véhicule */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {vehicle.make} {vehicle.model}
                  </Typography>
                  {vehicle.licensePlate && (
                    <Chip
                      label={vehicle.licensePlate}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>

                {/* Barre de timeline */}
                <Box
                  sx={{
                    position: 'relative',
                    height: 48,
                    bgcolor: '#fafafa',
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                  }}
                >
                  {/* Marqueurs d'heures en fond */}
                  {generateHourMarkers()}

                  {/* Time slots */}
                  {slots.map((slot, index) => {
                    const slotWidth = getSlotWidth(slot);
                    const showLabel = slotWidth > 15; // Afficher le label si > 15%

                    return (
                      <Tooltip
                        key={index}
                        title={formatSlotTooltip(slot)}
                        arrow
                        placement="top"
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${getTimePosition(slot.startTime)}%`,
                            width: `${slotWidth}%`,
                            height: '100%',
                            bgcolor: slot.isOccupied ? '#d32f2f' : '#2e7d32',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                            borderRight: '1px solid white',
                            '&:hover': {
                              opacity: 0.85,
                              transform: 'scaleY(1.05)',
                              zIndex: 1,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            },
                          }}
                        >
                          {showLabel && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              }}
                            >
                              {formatSlotTime(slot.startTime)} - {formatSlotTime(slot.endTime)}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            );
          })}

          {vehicles.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {t('reservation:calendar.noVehicles', 'Aucun véhicule à afficher')}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Note de bas de page */}
      <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          <strong>{t('reservation:calendar.timeRange', 'Plage horaire')}:</strong> {startHour}h00 - {endHour}h00
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          💡 {t('reservation:calendar.tip', 'Survolez les créneaux pour voir les détails (heures et durée)')}
        </Typography>
      </Box>
    </Box>
  );
};

export default FleetAvailabilityTimeline;
